import graphql from "babel-plugin-relay/macro";
import { LicenceType } from "components/licence/LicenceType";
import { LicenseTypeProvider } from "components/licence/LicenseTypeProvider";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { Message } from "shared/components/Message";
import { animalMessages } from "domain/animal/animalMessages";
import { bulkUpdateLicences, IBulkUpdateLicences, MutableLicenceSelectionInput } from "domain/animal/bulkUpdateLicences";
import { licenceMessages } from "domain/licence/licenceMessages";
import { paymentActions } from "domain/payment/paymentActions";
import { toastActions } from "domain/toast/toastActions";
import { BulkLicenceRenewalFormQuery, BulkLicenceRenewalFormQueryResponse } from "generatedQueries/BulkLicenceRenewalFormQuery.graphql";
import { bulkUpdateLicencesMutationResponse } from "generatedQueries/bulkUpdateLicencesMutation.graphql";
import moment from "moment";
import React, { FC } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { IEnvironment, PayloadError } from "relay-runtime";
import { routes } from "routes/routes";
import { ErrorHandlingMutationForm, IWarningMessageProps } from "shared/components/newFormInputs/ErrorHandlingMutationForm";
import { Query } from "shared/components/Query";
import { sharedMessages } from "shared/sharedMessages";
import { LoadingRegion } from "components/shared/LoadingRegion";
import { useFormContext, Path } from "react-hook-form";
import { required } from "shared/validations/validations";
import { calculateExpiryIso } from "components/licence/licenceExpiry";

type AnimalNode = NonNullable<NonNullable<NonNullable<BulkLicenceRenewalFormQueryResponse["account"]>["animals"]>["edges"]>[0]["node"];

interface IProps {
    animalIds: string[];
    onBack: () => void;
}

interface IBulkLicenceRowProps {
    index: number;
    animal: AnimalNode;
    jurisdictionId: number;
}

function BulkLicenceRow({ index, animal, jurisdictionId }: IBulkLicenceRowProps) {
    const { setValue, control } = useFormContext<IBulkUpdateLicences>();

    const typePath = `licenceSelections.${index}.licenceTypeId` as Path<IBulkUpdateLicences>;
    const expiryPath = `licenceSelections.${index}.licenceExpiry` as Path<IBulkUpdateLicences>;

    return (
        <tr>
            <td>{animal.name}</td>
            <td>
                <LicenseTypeProvider
                    animalTypeId={animal.animalTypeId}
                    jurisdictionId={jurisdictionId}
                    dateOfBirth={animal.dateOfBirth}
                    isDesexed={animal.isDesexed}
                    isRenewal={true}
                    isMicrochipped={isMicrochipped(animal)}
                >
                    {({ isLoading, licenseTypes }) => (
                        <LicenceType<IBulkUpdateLicences, Path<IBulkUpdateLicences>>
                            name={typePath}
                            control={control}
                            licenseTypes={licenseTypes}
                            isLoading={isLoading}
                            rules={required<IBulkUpdateLicences, Path<IBulkUpdateLicences>>()}
                            inputId={`licenceTypeId-${animal.id}`}
                            onSelected={(value) => {
                                const selected = licenseTypes.find(l => l.licenseTypeId === Number(value));
                                setValue(expiryPath, calculateExpiryIso(selected));
                            }}
                            aria-label={`License type for ${animal.name}`}
                        />
                    )}
                </LicenseTypeProvider>
            </td>
        </tr>
    );
}

export const BulkLicenceRenewalForm: FC<IProps> = ({
    onBack,
    animalIds,
}) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const history = useHistory();

    return <Query<BulkLicenceRenewalFormQuery>
        query={query}
        variables={{}}
        cacheConfig={{ force: true }}>
            {({ error, props: renderProps }) => (
                <LoadingRegion contentName={licenceMessages.selectLicensesTitle} hasError={!!error} isLoading={!renderProps}>
                    {(() => {
                    if (error) {
                        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
                    }

                    if (!renderProps) {
                        return null;
                    }

                    if (!renderProps.account) {
                        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
                    }

                    const animals = renderProps.account.animals?.edges?.map(edge => edge.node).filter(a => animalIds.indexOf(a.id) > -1) ?? [];

                    if (animals.length === 0) {
                        throw new Error("No animals have been selected for renewal");
                    }

                    const jurisdictionId = renderProps.account.physicalAddress?.jurisdictionId ?? 0;
                    const licenceSelections = getLicenseSelection(animals);

                    return <ErrorHandlingMutationForm<IBulkUpdateLicences, bulkUpdateLicencesMutationResponse>
                        onPrevious={onBack}
                        onNext={onFormCompleted}
                        defaultValues={licenceSelections}
                        mutationFunc={mutationFunc}
                        alwaysSubmit={true}
                        shouldDisplayWarning={shouldDisplayRabiesWarning(animals)}
                        toastSuccess={toastSuccess}
                        toastFailure={toastFailure}>
                        {() => (
                            <>
                                <h2 className="h3"><Message message={licenceMessages.selectLicensesTitle} /></h2>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th scope="col"><Message message={licenceMessages.renewalFormNameHeading} /></th>
                                            <th scope="col"><Message message={licenceMessages.renewalFormLicenseTypeHeading} /></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {licenceSelections.licenceSelections.map((s, index) => {
                                            const animal = animals.find(a => a.id === s.animalId);

                                            if (!animal) {
                                                return null;
                                            }

                                            return <BulkLicenceRow
                                                key={animal.id}
                                                index={index}
                                                animal={animal}
                                                jurisdictionId={jurisdictionId}
                                            />;
                                        })}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </ErrorHandlingMutationForm>;
                })()}
                </LoadingRegion>
            )}
    </Query>;

    function mutationFunc(
        environment: IEnvironment,
        bulkUpdateLicenceValues: IBulkUpdateLicences,
        onCompleted: (response: bulkUpdateLicencesMutationResponse, errors?: PayloadError[] | null) => void,
        onError: (error: Error) => void) {
        if (bulkUpdateLicenceValues.licenceSelections.length === 0) {
            throw new Error("There are no license selections for renewal");
        }

        bulkUpdateLicences(
            environment,
            bulkUpdateLicenceValues,
            onCompleted,
            onError);
    }

    function toastSuccess() {
        dispatch(toastActions.toastSuccess());
    }

    function toastFailure() {
        dispatch(toastActions.toastFailure());
    }

    function shouldDisplayRabiesWarning(animals: AnimalNode[]) {
        return (values: IBulkUpdateLicences): IWarningMessageProps[] | false => {
            const selectionsByAnimalId = new Map(
                values.licenceSelections.map(s => [s.animalId, s])
            );

            const selectionsWithRabiesWarnings = animals.filter(animal => {
                const selection = selectionsByAnimalId.get(animal.id);
                const rabiesExpiry = animal.rabiesVaccination?.expiryDate ? moment(animal.rabiesVaccination.expiryDate) : null;
                const expiryStr = selection?.licenceExpiry;

                return rabiesExpiry && expiryStr !== "LIFETIME" && expiryStr && rabiesExpiry.isBefore(moment(expiryStr));
            });

            if (selectionsWithRabiesWarnings.length > 0) {
                return [{
                    titleMessage: formatMessage(animalMessages.rabiesExpiryInvalidHeading),
                    warningMessage: formatMessage(selectionsWithRabiesWarnings.length === 1 ? animalMessages.rabiesExpiryInvalidError : animalMessages.rabiesExpiryMultipleInvalidError
                    ),
                }];
            }

            return false;
        };
    }

    function getLicenseSelection(animals: AnimalNode[]): IBulkUpdateLicences {
        return {
            licenceSelections: animals.map<MutableLicenceSelectionInput>(animal => ({
                animalId: animal.id,
                licenceTypeId: animal.licenceTypeId ?? undefined,
                licenceExpiry: getInitialLicenceExpiry(animal),
            })),
        };
    }

    function getInitialLicenceExpiry(animal: AnimalNode) {
        if (!animal.licenceType || !animal.licenceTypeId) {
            return undefined;
        }

        return calculateExpiryIso({
            licenseTypeId: animal.licenceTypeId,
            description: "",
            isLifetime: animal.licenceType.isLifetime,
            expiryMonths: animal.licenceType.expiryMonths ?? null,
            expiryDate: animal.licenceType.expiryDate
                ? {
                    calculatedExpiryDate: getCalculatedExpiryDate(animal.licenceType.expiryDate),
                }
                : null,
        });
    }

    function getCalculatedExpiryDate(expiryDate: { day: number; month: number; years: number }): string {
        let expiry = moment({
            year: moment().year(),
            month: expiryDate.month - 1,
            day: expiryDate.day,
        });

        if (expiry.isSameOrBefore(moment(), "days")) {
            expiry = expiry.add(1, "year");
        }

        if (expiryDate.years > 1) {
            expiry = expiry.add(expiryDate.years - 1, "year");
        }

        return expiry.format("YYYY-MM-DD");
    }

    function onFormCompleted() {
        dispatch(paymentActions.addRenewingAnimalsToCart(animalIds));
        history.push(routes.payment.index.create({}));
    }
};

function isMicrochipped(animal: AnimalNode): boolean {
    if (!animal.identifications || animal.identifications.length === 0) {
        return false;
    }
    return animal.identifications.find(i => i && i.type === "MICROCHIP") !== undefined;
}

const query = graphql`
query BulkLicenceRenewalFormQuery {
    account {
        animals {
            edges {
                node {
                    id
                    name
                    animalTypeId
                    isDesexed
                    rabiesVaccination {
                        expiryDate
                    }
                    licenceTypeId
                    licenceType {
                        expiryDate {
                            day
                            month
                            years
                        }
                        expiryMonths
                        isLifetime
                    }
                    identifications {
                        type
                    }
                    dateOfBirth
                }
            }
        }
        physicalAddress {
            jurisdictionId
        }
    }
}`;
